import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import ModalCustom from '../../UI/modal';
import Button from '../../UI/button';
import { useTrip } from '../../contexts/trip-context';
import tripActivityService from '../../../services/trips/trip-activity.service';

interface Props {
  show: boolean;
  handleClose: () => void;
  activityId: string;
  activityTitle: string;
  currentFolderId: string;
  currentDestinationId: string;
}

interface FolderOption {
  id: string;
  title: string;
  destinationId: string;
  destinationTitle: string;
  level: number;
}

const MoveActivityToFolderModal: React.FC<Props> = ({
  show,
  handleClose,
  activityId,
  activityTitle,
  currentFolderId,
  currentDestinationId
}) => {
  const { trip, setRefreshTrip } = useTrip();
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [selectedDestinationId, setSelectedDestinationId] =
    useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [folderOptions, setFolderOptions] = useState<FolderOption[]>([]);
  const [error, setError] = useState<string>('');

  // Build folder list from trip folders
  useEffect(() => {
    if (!trip || !show) return;

    const options: FolderOption[] = (trip.folders ?? []).map((folder) => ({
      id: folder.id,
      title: folder.title ?? 'Unnamed Folder',
      destinationId: '',
      destinationTitle: '',
      level: 0
    }));

    // Remove current folder from options
    const filtered = options.filter((opt) => opt.id !== currentFolderId);
    setFolderOptions(filtered);
  }, [trip, show, currentFolderId]);

  const handleMove = async () => {
    if (!selectedFolderId || !trip) return;

    setIsLoading(true);
    setError('');

    try {
      await tripActivityService.moveActivityToFolder(
        trip.id ?? '',
        selectedDestinationId,
        activityId,
        currentFolderId,
        selectedFolderId
      );

      setRefreshTrip(true);
      handleClose();
    } catch (error: any) {
      console.error('Failed to move activity:', error);
      setError(error?.message || 'Failed to move activity');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFolderChange = (folderId: string) => {
    setSelectedFolderId(folderId);
    const folder = folderOptions.find((opt) => opt.id === folderId);
    if (folder) {
      setSelectedDestinationId(folder.destinationId);
    }
  };

  return (
    <ModalCustom
      show={show}
      onHide={handleClose}
      title={`Move "${activityTitle}" to Folder`}
    >
      <Form className='move-activity-to-folder-modal'>
        <p
          className='fs-6 font-libre'
          style={{ marginBottom: '4px' }}
        >
          Target Folder
        </p>
        <Form.Select
          style={{ marginBottom: '24px' }}
          value={selectedFolderId}
          onChange={(e) => handleFolderChange(e.target.value)}
          disabled={isLoading}
        >
          <option value=''>Select destination or folder...</option>
          {folderOptions.map((option) => (
            <option
              key={option.id}
              value={option.id}
            >
              {'  '.repeat(option.level)}
              {option.level > 0 ? '└─ ' : ''}
              {option.title}
              {option.level === 0 ? ' (Destination)' : ''}
            </option>
          ))}
        </Form.Select>

        {error && (
          <div
            className='alert alert-danger'
            role='alert'
          >
            {error}
          </div>
        )}

        <div className='d-flex gap-2 justify-content-end'>
          <Button
            variant='secondary'
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleMove}
            disabled={!selectedFolderId || isLoading}
          >
            {isLoading ? 'Moving...' : 'Move'}
          </Button>
        </div>
      </Form>
    </ModalCustom>
  );
};

export default MoveActivityToFolderModal;
